import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import _ from 'lodash';
import { formatPrice } from 'utilities/strings';
import pluralize from 'pluralize';

import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControl from '@material-ui/core/FormControl';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';

import TruckFastIcon from 'mdi-material-ui/TruckFast';

import CircularLoader from 'components/CircularLoader';

import { CheckoutFormControlLabel, CheckoutCollapse } from './CheckoutRadio';

import {
  getOrderShippingEstimates,
  updateOrderShippingMethod
} from 'features/checkout/actions';

const NextButton = styled(Button)`
  margin-top: 24px;
`;

const ShippingTextField = styled(TextField)`
  margin-bottom: 24px;
`;

const MutedTruckFastIcon = styled(TruckFastIcon)`
  opacity: 0.5;
`;

const ShippingEstimate = styled.div`
  padding: 14px 0;
  margin: 14px 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-width: 1px 0;
  display: flex;

  ${MutedTruckFastIcon} {
    margin-right: 14px;
    width: 48px;
    height: 48px;
  }
`;

const shippingMethodMap = {
  FLAT: 'FLAT',
  CUSTOM: 'CUSTOM'
};

const ShippingSelector = ({
  activeStep,
  order,
  shippingEstimates,
  loading,
  estimatesLoading,
  methodResponse,
  methodLoading,
  methodError,
  getOrderShippingEstimates,
  updateOrderShippingMethod
}) => {
  useEffect(
    () => {
      getOrderShippingEstimates(order.token);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const orderShippingMethod = _.get(order, 'groups[0].shipping_method_name');
  const orderAccountNumber = _.get(order, 'groups[0].carrier_account_number');
  const orderShippingCarrier = _.get(order, 'groups[0].shipping_carrier');

  const [selectedShippingOption, setSelectedShippingOption] = useState(
    shippingMethodMap[orderShippingMethod]
  );
  const [accountNumber, setAccountNumber] = useState(orderAccountNumber);
  const [shippingCarrier, setShippingCarrier] = useState(orderShippingCarrier);

  const handleShippingOptionChange = event => {
    setSelectedShippingOption(String(event.target.value));
    setAccountNumber('');
    setShippingCarrier('');
  };

  const handleAccountChange = event => {
    setAccountNumber(event.target.value);
  };

  const handleCarrierChange = event => {
    setShippingCarrier(event.target.value);
  };

  const goToNext = () => {
    updateOrderShippingMethod(
      order.token,
      selectedShippingOption,
      accountNumber,
      shippingCarrier
    );
  };

  return loading || estimatesLoading ? (
    <CircularLoader />
  ) : (
    <>
      {order && (
        <Grid container>
          <Grid item xs={12}>
            <FormControl component="fieldset" fullWidth>
              {/* Temporarily hide shipping estimate with impossible equality test */}
              {1 == 2 && !order.has_cells && (
                <ShippingEstimate>
                  <MutedTruckFastIcon />
                  <div>
                    <Typography variant="subtitle2">
                      Estimated Ship Date:
                    </Typography>
                    <Typography variant="h6" gutterBottom inline>
                      <span id="days-to-ship">
                        {shippingEstimates.days_to_ship}
                      </span>{' '}
                      {pluralize('day', shippingEstimates.days_to_ship)} After
                      Receipt of Payment
                    </Typography>
                  </div>
                </ShippingEstimate>
              )}
              {methodError && (
                <Typography variant="subtitle2" color="error" gutterBottom>
                  {methodError.non_field_errors ||
                    'Problem updating shipping method'}
                </Typography>
              )}
              <RadioGroup
                id="shipping-selector-radiogroup"
                value={selectedShippingOption}
                onChange={handleShippingOptionChange}
              >
                <CheckoutFormControlLabel
                  id="flat-rate-shipping-radiobutton"
                  label={
                    <>
                      <strong>
                        Flat Rate Shipping via {shippingEstimates.provider}
                      </strong>{' '}
                      -{formatPrice(shippingEstimates.shipping_rate)}
                    </>
                  }
                  value="FLAT"
                  control={<Radio color="primary" />}
                  disabled={methodLoading}
                />
                <CheckoutFormControlLabel
                  id="carrier-account-shipping-radiobutton"
                  label={<strong>Use Your Own Carrier Account</strong>}
                  value="CUSTOM"
                  control={<Radio color="primary" />}
                  disabled={methodLoading}
                  withCollapse
                />
              </RadioGroup>
              <CheckoutCollapse in={selectedShippingOption === 'CUSTOM'}>
                <Grid container>
                  <Grid item xs={12} md={4}>
                    <FormControl style={{ minWidth: 160 }}>
                      <ShippingTextField
                        name="users-carrier"
                        label="Shipping Carrier"
                        margin="dense"
                        variant="filled"
                        value={shippingCarrier}
                        onChange={handleCarrierChange}
                        disabled={methodLoading}
                        select
                      >
                        <MenuItem value="FedEx">FedEx</MenuItem>
                        <MenuItem value="DHL">DHL</MenuItem>
                        <MenuItem value="World_Courier">World Courier</MenuItem>
                      </ShippingTextField>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <ShippingTextField
                      name="users-account-number"
                      label="Carrier Account Number"
                      margin="dense"
                      variant="filled"
                      value={accountNumber}
                      onChange={handleAccountChange}
                      disabled={methodLoading}
                    />
                  </Grid>
                </Grid>
              </CheckoutCollapse>

              <NextButton
                id="shipping-selector-submit"
                onClick={() => goToNext()}
                variant="contained"
                color="primary"
                disabled={methodLoading || !selectedShippingOption}
              >
                Use This Shipping Method
              </NextButton>
            </FormControl>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default connect(
  state => ({
    activeStep: state.checkout.activeStep,
    order: state.checkout.order,
    shippingEstimates: state.checkout.shippingEstimates,
    loading: state.checkout.orderLoading,
    estimatesLoading: state.checkout.estimatesLoading,
    methodError: state.checkout.methodError,
    methodResponse: state.checkout.methodResponse,
    methodLoading: state.checkout.methodLoading,
    error: state.checkout.error
  }),
  {
    updateOrderShippingMethod,
    getOrderShippingEstimates
  }
)(ShippingSelector);
